import type { ComponentProps, ReactNode } from 'react'

import { XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import type { TagColor } from '@/modules/graphql/types/tag'

// Tag silhouette: chevron point on the left, flat top/bottom, rounded
// top-right and bottom-right corners, vertical right edge between them.
// Drawn as a borderless inline SVG behind the content.
const TAG_PATH = 'M0,15 L8,0 L94,0 Q100,0 100,6 L100,24 Q100,30 94,30 L8,30 Z'

// Categorical "data-viz" palette — single source-of-truth hex per
// color. The fill is a 40% mix of the color with white (an opaque
// pastel); text is a 70% mix with black so it stays legible on every
// fill regardless of how light/dark the source hue is.
const COLOR_HEX: Record<TagColor, string> = {
  AGED_TERRACOTTA: '#d77655',
  BLOOMING_VIOLET: '#bb15b7',
  BLUE_GEYSER: '#1ceaf9',
  CITRUS_SPROUT: '#caf243',
  DEEP_WISTERIA: '#4d57a8',
  FOGGY_FJORD: '#82a0ca',
  FOREST_HERB: '#458612',
  LIME_HUSK: '#d8e9b2',
  MINT_MEADOW: '#83e9a3',
  NEON_GROVE: '#83fe3f',
  OCEAN_KELP: '#256676',
  PETAL_ORCHID: '#d595d9',
  SAGEBRUSH: '#9aa27a',
  TWILIGHT_PETAL: '#562fff',
  WILD_ROSEHIP: '#f43a54',
  WILD_THISTLE: '#fe74fe',
}

type BaseTagChipProps = Omit<ComponentProps<'span'>, 'children'> & {
  color: TagColor
  children: ReactNode
}

// Discriminated union: callers passing `onRemove` MUST also supply a
// `removeLabel`. Stops the X button from rendering without an
// accessible name.
type RemovableTagChipProps = BaseTagChipProps & {
  onRemove: () => void
  removeLabel: string
}

type StaticTagChipProps = BaseTagChipProps & {
  onRemove?: undefined
  removeLabel?: never
}

export type TagChipProps = RemovableTagChipProps | StaticTagChipProps

export const TagChip = ({
  color,
  className,
  children,
  style,
  onRemove,
  removeLabel,
  ...props
}: TagChipProps) => {
  const hex = COLOR_HEX[color]
  const fill = `color-mix(in oklch, ${hex} 40%, white)`
  const text = `color-mix(in oklch, ${hex} 70%, black)`
  return (
    <span
      {...props}
      className={cn(
        'relative inline-flex w-fit shrink-0 items-center gap-1 px-4 py-1 text-xs font-medium whitespace-nowrap',
        className,
      )}
      style={{ color: text, ...style }}
    >
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 size-full"
        preserveAspectRatio="none"
        viewBox="0 0 100 30"
      >
        <path d={TAG_PATH} fill={fill} />
      </svg>
      <span className="relative">{children}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          onMouseDown={(e) => e.preventDefault()}
          className="relative -mr-1.5 inline-flex size-4 items-center justify-center rounded-full hover:bg-black/10"
        >
          <XIcon className="size-3" />
        </button>
      )}
    </span>
  )
}
